import { toast } from '@/components/ui/sonner';
import type { Notification } from '@/types/notification';
import type { NavigateFn } from '@tanstack/react-router';
import {
  getNotificationActions,
  getNotificationToastDuration,
  getNotificationUrgency,
  getNotificationUrgencyEmoji,
} from '@/utils/notification-navigation';

// Module-level Set of notification IDs that have already been toasted.
// Persists across StrictMode re-mounts and prevents double-toasts from
// multiple callers (realtime INSERT + array effect fallback).
const toastedNotificationIds = new Set<string>();

/**
 * Show a Sonner toast for a notification with actionable navigation.
 *
 * Uses the notification ID as the Sonner toast ID, and also tracks IDs in a
 * module-level Set, so the same notification cannot be toasted twice from any
 * caller (StrictMode, re-renders, realtime events, etc.).
 */
export function showNotificationToast(
  notification: Notification,
  navigate: NavigateFn
): string | null {
  const toastId = `notification-${notification.id}`;

  if (toastedNotificationIds.has(notification.id)) {
    return null;
  }

  toastedNotificationIds.add(notification.id);

  const actions = getNotificationActions(notification);
  const urgency = getNotificationUrgency(notification);

  toast.success(
    `${getNotificationUrgencyEmoji(urgency)} ${notification.title || notification.message_preview || 'New notification'}`,
    {
      id: toastId,
      description: notification.actor_id
        ? `From ${notification.actor?.full_name || notification.actor?.username || notification.actor_id}`
        : (notification.title ? notification.message_preview : undefined),
      duration: getNotificationToastDuration(urgency),
      action: actions.primary
        ? {
            label: actions.primary.label,
            onClick: () => {
              if (actions.primary!.external) {
                window.open(actions.primary!.url, '_blank', 'noopener,noreferrer');
              } else {
                navigate({ href: actions.primary!.url });
              }
            },
          }
        : {
            label: 'View',
            onClick: () => {
              navigate({ to: '/notifications' });
            },
          },
      cancel:
        actions.secondary && actions.secondary.length > 0
          ? {
              label: actions.secondary[0].label,
              onClick: () => {
                const secondaryAction = actions.secondary![0];
                if (secondaryAction.external) {
                  window.open(secondaryAction.url, '_blank', 'noopener,noreferrer');
                } else {
                  navigate({ href: secondaryAction.url });
                }
              },
            }
          : undefined,
      className:
        urgency === 'critical'
          ? 'border-red-500 bg-red-50 dark:bg-red-950'
          : urgency === 'high'
            ? 'border-orange-500 bg-orange-50 dark:bg-orange-950'
            : urgency === 'medium'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
              : undefined,
    }
  );

  return toastId;
}

/**
 * Mark a notification ID as already toasted without showing a toast.
 * Useful when the caller wants to suppress a toast for a notification it has
 * already handled (e.g., marking as read immediately after insertion).
 */
export function markNotificationToasted(notificationId: string): void {
  toastedNotificationIds.add(notificationId);
}

/**
 * Reset the set of toasted notification IDs. Intended for tests only.
 */
export function __resetToastedNotificationIds(): void {
  toastedNotificationIds.clear();
}
